import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';
import { useServerTime } from 'hooks/useServerTime';
import {
  fetchExperienceCatalogList,
  getDeadLineExperienceCatalog,
  getRegulationExperienceCatalog,
  loadExperiencePurchase,
  purchaseItemExperienceCatalog,
  undoExperiencePurchase,
} from 'services/experienceCataolog.service';
import { getNamedDates } from 'services/utils';
import { ExperienceCatalogResponse } from 'types/experienceCatalog';
import { readCatalogPeriod, resolveCatalogPeriodState } from 'utils/catalog-period';
import { canAccessExperienceCatalog } from 'utils/experience-catalog';

import {
  CartaoDeExperiencia,
  EstadoDaVitrine,
  FiltroDoCatalogo,
  ResgateDoCiclo,
  filtrarCartoes,
  idDaEscolha,
  itemDaEscolhaAtual,
  montarCartoes,
} from './catalogo-modelo';
import AvisoDeImpedimento from './components/AvisoDeImpedimento';
import CabecalhoDoCatalogo from './components/CabecalhoDoCatalogo';
import ErroDeCarga from './components/ErroDeCarga';
import HeroDoCatalogo from './components/HeroDoCatalogo';
import ListaDeExperiencias from './components/ListaDeExperiencias';
import ModalDeResgate from './components/ModalDeResgate';
import ModalLivelo from './components/ModalLivelo';
import PrazoDaEscolha from './components/PrazoDaEscolha';
import QuadroDaEscolha from './components/QuadroDaEscolha';
import SemExperiencias from './components/SemExperiencias';
import { montarExperienciaEscolhida } from './feedbackPage/experiencia-escolhida';
import { CicloComInicio, useCicloDoCatalogo } from './hooks/useCicloDoCatalogo';
import { useCargaPorChave } from 'hooks/useCargaPorChave';
import { GENERIC_REFUSAL_MESSAGE, PurchaseOutcome, redeemExperience } from './purchaseOutcome';
import { DesfechoDaTroca, trocarExperiencia } from './trocaDeExperiencia';

const mensagensDeRecusa: Record<string, string> = {
  insufficient_requirements: 'Estrelas insuficientes para realizar o resgate.',
  exceeded_purchase_limit: 'Limite de resgate da experiência atingido.',
  item_does_not_exist: 'Esta experiência não existe mais.',
  player_does_not_exist: 'O player informado para o resgate não existe.',
  item_disabled: 'Esta experiência está desabilitada.',
  item_out_of_time: 'Ultrapassado o período para resgate da experiência.',
  limit_exceeded: 'Você já realizou o resgate desta experiência.',
  item_sold_out: 'Esta experiência está esgotada.',
  principal_not_allowed: 'Você não tem permissão para realizar o resgate.',
  // Recusa da trigger `FnEN39m` quando a janela do ciclo não está aberta. O código é o mesmo
  // string gravado lá; sem esta linha a recusa cairia na mensagem genérica de erro.
  catalog_period_closed: 'O período de resgate deste ciclo não está aberto.',
};

interface DadosDaVitrine {
  catalogo?: ExperienceCatalogResponse;
  inicio?: Date;
  fim?: Date;
  regulamentoUrl: string;
  /** Resgate ativo do jogador no ciclo; `null` sem resgate ou quando a leitura falha. */
  resgate?: ResgateDoCiclo | null;
}

/** Falha na leitura do resgate não derruba a vitrine: ela aparece como para quem não resgatou (TRC-09). */
async function lerResgate(chave: string, ciclo: CicloComInicio): Promise<ResgateDoCiclo | null> {
  try {
    return await loadExperiencePurchase(chave, ciclo.title);
  } catch (erro) {
    console.error('Erro ao ler o resgate do ciclo:', erro);
    return null;
  }
}

/** Catálogo, período, regulamento e resgate do ciclo. */
async function carregarVitrine(chave: string, ciclo: CicloComInicio): Promise<DadosDaVitrine> {
  const [catalogo, deadLineResponse, regulamento, resgate] = await Promise.all([
    fetchExperienceCatalogList(chave, ciclo.title),
    getDeadLineExperienceCatalog(ciclo._id),
    getRegulationExperienceCatalog(ciclo.title),
    lerResgate(chave, ciclo),
  ]);

  // As duas datas saem só de `startDate` e `endDate` (VIG-14). Sem data legível, a página fica sem
  // contagem: chutar "agora" mandaria o jogador para o feedback como se o prazo tivesse acabado.
  const periodo = readCatalogPeriod(deadLineResponse);
  if (!periodo.end) console.error('endDate ausente ou ilegível no período do ciclo:', deadLineResponse?.endDate);
  if (!periodo.start) console.error('startDate ausente ou ilegível no período do ciclo:', deadLineResponse?.startDate);

  return { catalogo, inicio: periodo.start, fim: periodo.end, regulamentoUrl: regulamento?.file?.url || '', resgate };
}

function Esqueleto() {
  return (
    <div aria-busy="true" className="flex flex-col gap-5">
      <p className="sr-only" role="status">
        Carregando o catálogo de experiências
      </p>
      <div className="h-[340px] animate-pulse rounded-[20px] bg-surface-200 motion-reduce:animate-none" />
      <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-[22px]">
        {[0, 1, 2].map(i => (
          <div key={i} className="h-[556px] animate-pulse rounded-xl bg-surface-200 motion-reduce:animate-none" />
        ))}
      </div>
    </div>
  );
}

export default function ExperienceCatalog() {
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const { ciclos, cicloId, ciclo, trocarCiclo } = useCicloDoCatalogo();
  // Relógio do SERVIDOR: decide a vigência, a contagem e a consulta depois do prazo.
  const { realDatetime: agora, hasTime: temHoraDoServidor } = useServerTime({ tick: true });

  const aguardandoAbertura = useRef(false);
  const [filtro, setFiltro] = useState<FiltroDoCatalogo>('todas');
  const [viradoId, setViradoId] = useState<string>();
  const [escolhido, setEscolhido] = useState<CartaoDeExperiencia>();
  const [promocoesAbertas, setPromocoesAbertas] = useState(false);
  // Pedido enviado sem desfecho conhecido, ou troca: ao fechar o modal, a vitrine relê o que ficou gravado.
  const releituraAoFechar = useRef(false);

  // Sem acesso ao catálogo, nada é buscado: a página mostra o texto de ausência de itens.
  const temAcesso = !!userInfo?.chave && canAccessExperienceCatalog(userInfo);
  const { carga, recarregar, recargas } = useCargaPorChave<DadosDaVitrine>(
    userInfo && ciclo ? `${ciclo._id}|${userInfo.chave ?? ''}|${temAcesso}` : undefined,
    () => (temAcesso && userInfo && ciclo ? carregarVitrine(userInfo.chave, ciclo) : Promise.resolve({ regulamentoUrl: '' })),
  );
  const dados = carga.estado === 'pronto' ? carga.dados : undefined;

  /*
   * Quem já resgatou no ciclo entra pela página da experiência; a lista abre quando o jogador a pede de lá
   * ("Ver experiências do ciclo", que chega com `verLista`). Só na primeira carga: trocar de ciclo ou
   * fechar o modal da troca não tira o jogador da lista.
   */
  const pediuALista = !!(useLocation().state as EstadoDaVitrine | null)?.verLista;
  const entradaDecidida = useRef(false);
  useEffect(() => {
    if (!dados || entradaDecidida.current) return;
    entradaDecidida.current = true;
    if (dados.resgate && !pediuALista) navigate('feedback', { replace: true });
  }, [dados, pediuALista, navigate]);

  useEffect(() => {
    setViradoId(undefined);
  }, [ciclo?._id, recargas]);

  /*
   * Onde o relógio do SERVIDOR cai na janela do ciclo. Um navegador atrasado abriria a vitrine
   * antes da hora, e um adiantado esconderia o catálogo aberto. Sem a hora do servidor, nada é decidido.
   */
  const estadoDoPeriodo = temHoraDoServidor ? resolveCatalogPeriodState(agora, { start: dados?.inicio, end: dados?.fim }) : 'desconhecido';

  // VIG-11: na transição de "antes" para "aberto", a página busca o catálogo de novo, uma vez.
  useEffect(() => {
    if (estadoDoPeriodo === 'antes') {
      aguardandoAbertura.current = true;
      return;
    }
    if (estadoDoPeriodo === 'aberto' && aguardandoAbertura.current) {
      aguardandoAbertura.current = false;
      recarregar();
    }
  }, [estadoDoPeriodo, recarregar]);

  // Passado o prazo, a vitrine fica no lugar, só para consulta (TRC-18, TRC-20).
  const somenteConsulta = estadoDoPeriodo === 'encerrado';

  const trocarFiltro = (novo: FiltroDoCatalogo) => {
    setFiltro(novo);
    setViradoId(undefined);
  };

  /*
   * Sem confirmação, a página confere se o resgate gravou mesmo assim: ao fechar o modal, ela relê
   * resgate e catálogo, e o quadro "Sua escolha" mostra o que ficou (RES-21, agora sem navegar: TRC-06).
   */
  const conferirResgate = async () => {
    releituraAoFechar.current = true;
  };

  const fecharModal = () => {
    setEscolhido(undefined);
    if (!releituraAoFechar.current) return;
    releituraAoFechar.current = false;
    recarregar();
  };

  /*
   * O resultado volta para o modal. Resgate gravado não navega daqui: o modal mostra o "Escolha
   * registrada!" e o jogador decide para onde ir (CAT-23).
   */
  const resgatar = async (cartao: CartaoDeExperiencia): Promise<PurchaseOutcome> => {
    const playerId = userInfo?.chave;
    if (!playerId) return { kind: 'recusada', message: GENERIC_REFUSAL_MESSAGE };

    return redeemExperience({
      purchase: () => purchaseItemExperienceCatalog(cartao.item, playerId),
      restrictionMessages: mensagensDeRecusa,
      onUnconfirmed: conferirResgate,
    });
  };

  const cartoes = montarCartoes(dados?.catalogo);
  const resgate = dados?.resgate;
  const nomeDaEscolhaAtual = montarExperienciaEscolhida(resgate)?.nome;

  // Troca (TRC-12 a TRC-16): cancela a escolha atual e resgata o cartão; se a nova não vier, devolve a anterior.
  const trocar = async (cartao: CartaoDeExperiencia): Promise<DesfechoDaTroca> => {
    const playerId = userInfo?.chave;
    const anterior = itemDaEscolhaAtual(cartoes, resgate);
    if (!playerId || !ciclo || !anterior) return { kind: 'cancelamento_falhou', mensagem: GENERIC_REFUSAL_MESSAGE };

    // Qualquer desfecho da troca muda ou pode ter mudado o resgate: ao fechar, a vitrine relê (TRC-17).
    releituraAoFechar.current = true;
    return trocarExperiencia({
      cancelar: () => undoExperiencePurchase(ciclo.title),
      comprar: () => purchaseItemExperienceCatalog(cartao.item, playerId),
      comprarAnterior: () => purchaseItemExperienceCatalog(anterior, playerId),
      restrictionMessages: mensagensDeRecusa,
    });
  };

  // O aviso de pagamento e "não há experiências" só aparecem com a hora do servidor em mãos e
  // fora da fase de abertura (VIG-10): sem a hora, a página ainda não sabe se o catálogo abriu, e
  // os dois piscariam até ela chegar. Sem período cadastrado (VIG-13), eles aparecem.
  const podeFalarDoCiclo = temHoraDoServidor && estadoDoPeriodo !== 'antes';

  return (
    <div className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0">
      <CabecalhoDoCatalogo ciclos={ciclos} cicloId={cicloId} onCiclo={trocarCiclo} />

      {carga.estado === 'carregando' && <Esqueleto />}

      {carga.estado === 'erro' && <ErroDeCarga mensagem="Não foi possível carregar o catálogo deste ciclo." onTentarDeNovo={recarregar} />}

      {dados && (
        <>
          <HeroDoCatalogo
            regulamentoUrl={dados.regulamentoUrl}
            // Sem a hora do servidor, nada de contagem nem barra: o relógio do navegador pode estar errado.
            prazo={
              <PrazoDaEscolha
                estado={estadoDoPeriodo}
                agora={agora}
                inicio={temHoraDoServidor ? dados.inicio : undefined}
                fim={temHoraDoServidor ? dados.fim : undefined}
              />
            }
          />

          {/* Antes da abertura, nem o aviso de pagamento nem "não há experiências" (VIG-10). */}
          {podeFalarDoCiclo && (ciclo?.date_start ?? 0) >= getNamedDates().ciclo_2S2025 && <AvisoDeImpedimento />}

          {nomeDaEscolhaAtual && <QuadroDaEscolha nome={nomeDaEscolhaAtual} />}

          {cartoes.length > 0 ? (
            <ListaDeExperiencias
              cartoes={filtrarCartoes(cartoes, filtro)}
              filtro={filtro}
              onFiltro={trocarFiltro}
              viradoId={viradoId}
              onVirar={setViradoId}
              onConfirmar={setEscolhido}
              onPromocoesLivelo={() => setPromocoesAbertas(true)}
              escolhidoId={idDaEscolha(resgate)}
              somenteConsulta={somenteConsulta}
            />
          ) : (
            podeFalarDoCiclo && <SemExperiencias />
          )}

          <ModalDeResgate
            cartao={escolhido}
            fim={dados.fim}
            regulamentoUrl={dados.regulamentoUrl}
            resgatar={resgatar}
            escolhaAtual={nomeDaEscolhaAtual}
            trocar={trocar}
            onFechar={fecharModal}
            onVerExperiencia={() => navigate('feedback')}
          />
          <ModalLivelo isOpen={promocoesAbertas} onFechar={() => setPromocoesAbertas(false)} />
        </>
      )}
    </div>
  );
}
